import { type Candidate, type CandidateStatus } from "@vidario/domain";
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { useVidarioClient } from "../client";
import { queryKeys } from "../queryKeys";

export interface UpdateCandidateStatusInput {
  /** Required for cache invalidation. The candidate response carries
   * the job FK as `job` (not `jobId`), so callers must pass the
   * URL-derived jobId from useParams / parent prop. Same pattern as
   * useUpdateCandidate. */
  jobId: string;
  candidateId: string;
  status: CandidateStatus;
  rejectionReason?: string | null;
  /** For a move back to the Longlist (#162). */
  reason?: string | null;
}

export function useUpdateCandidateStatus(): UseMutationResult<
  Candidate,
  Error,
  UpdateCandidateStatusInput
> {
  const client = useVidarioClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, status, rejectionReason, reason }) =>
      client.updateCandidateStatus({
        candidateId,
        status,
        ...(rejectionReason !== undefined ? { rejectionReason } : {}),
        ...(reason !== undefined ? { reason } : {}),
      }),
    onSuccess: (candidate, { jobId, candidateId }) => {
      qc.setQueryData(queryKeys.candidate(jobId, candidateId), candidate);
      qc.invalidateQueries({ queryKey: ["jobs", jobId, "candidates"] });
      // Status changes append an entry to candidate_log (server-side
      // in CandidateService.transition_status), so refetch the log so
      // the new entry appears without a reload.
      qc.invalidateQueries({ queryKey: ["candidates", candidateId, "log"] });
    },
  });
}
